import { ref, computed, type Ref } from 'vue'
import { useRouter, type Router } from 'vue-router'
import type { MenuItem, MenuGroup } from '@/config/menuConfig'

export function useSidebarHelpers(router: Router) {
  // Helper to check if a route exists
  function routeExists(path: string): boolean {
    try {
      const resolved = router.resolve(path)
      return resolved.matched.length > 0 && resolved.name !== 'not-found'
    } catch (e) {
      return false
    }
  }

  // Filter menu items to only show valid routes
  function filterValidMenuItems(items: MenuItem[]): MenuItem[] {
    return items.filter((item) => {
      // Always show items with children or if route exists
      if (item.children && item.children.length > 0) {
        item.children = filterValidMenuItems(item.children)
        return item.children.length > 0
      }
      return routeExists(item.path)
    })
  }

  // Check if a route is currently active
  function isRouteActive(item: MenuItem): boolean {
    const currentPath = router.currentRoute.value.path
    if (item.exact) {
      return currentPath === item.path
    }
    return currentPath.startsWith(item.path)
  }

  // Find menu item by path
  function findMenuItemByPath(path: string, groups: MenuGroup[]): MenuItem | null {
    for (const group of groups) {
      for (const item of group.items) {
        if (item.path === path) return item
        if (item.children) {
          const child = item.children.find((c) => c.path === path)
          if (child) return child
        }
      }
    }
    return null
  }

  // Fuzzy search matching
  function fuzzyMatch(text: string, pattern: string): boolean {
    if (text.includes(pattern)) return true
    const textLower = text.toLowerCase()
    const patternLower = pattern.toLowerCase()
    let patternIndex = 0
    for (let i = 0; i < textLower.length && patternIndex < patternLower.length; i++) {
      if (textLower[i] === patternLower[patternIndex]) {
        patternIndex++
      }
    }
    return patternIndex === patternLower.length
  }

  return {
    routeExists,
    filterValidMenuItems,
    isRouteActive,
    findMenuItemByPath,
    fuzzyMatch,
  }
}

export function useSidebarPersistence() {
  // Save and load favorites
  function saveFavorites(items: MenuItem[]) {
    localStorage.setItem('sidebar-favorites', JSON.stringify(items))
  }

  function loadFavorites(): MenuItem[] {
    try {
      const favorites = localStorage.getItem('sidebar-favorites')
      return favorites ? JSON.parse(favorites) : []
    } catch (error) {
      console.warn('Failed to load favorites:', error)
      return []
    }
  }

  // Save and load recent items
  function saveRecent(items: MenuItem[]) {
    localStorage.setItem('sidebar-recent', JSON.stringify(items))
  }

  function loadRecent(): MenuItem[] {
    try {
      const recent = localStorage.getItem('sidebar-recent')
      return recent ? JSON.parse(recent) : []
    } catch (error) {
      console.warn('Failed to load recent items:', error)
      return []
    }
  }

  // Save and load collapsed groups
  function saveCollapsedGroups(groups: Set<number>) {
    localStorage.setItem('sidebar-collapsed-groups', JSON.stringify(Array.from(groups)))
  }

  function loadCollapsedGroups(): Set<number> {
    try {
      const collapsed = localStorage.getItem('sidebar-collapsed-groups')
      return collapsed ? new Set(JSON.parse(collapsed)) : new Set()
    } catch (error) {
      console.warn('Failed to load collapsed groups:', error)
      return new Set()
    }
  }

  // Save and load expanded items
  function saveExpandedItems(items: Set<string>) {
    localStorage.setItem('sidebar-expanded-items', JSON.stringify(Array.from(items)))
  }

  function loadExpandedItems(): Set<string> {
    try {
      const expanded = localStorage.getItem('sidebar-expanded-items')
      return expanded ? new Set(JSON.parse(expanded)) : new Set()
    } catch (error) {
      console.warn('Failed to load expanded items:', error)
      return new Set()
    }
  }

  return {
    saveFavorites,
    loadFavorites,
    saveRecent,
    loadRecent,
    saveCollapsedGroups,
    loadCollapsedGroups,
    saveExpandedItems,
    loadExpandedItems,
  }
}
